// WebSocketClient.js
// A client-side WebSocket manager to handle real-time communication with the server
import { getWebSocketUrl } from './utils/apiUtils';

class WebSocketClient {
  constructor() {
    this.socket = null;
    this.connectionActive = false; // Renamed from isConnected to avoid collision
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectInterval = 3000; // 3 seconds
    this.listeners = new Map();
    this.pendingMessages = [];
    this.serverUrl = null;
  }

  // Connect to the WebSocket server
  connect(customUrl = null) {
    // Use either the provided URL or get the appropriate WebSocket URL from apiUtils
    this.serverUrl = customUrl || getWebSocketUrl();
    
    console.log(`Connecting to WebSocket server at ${this.serverUrl}`);
    
    try {
      this.socket = new WebSocket(this.serverUrl);
      
      this.socket.onopen = this.handleOpen.bind(this);
      this.socket.onmessage = this.handleMessage.bind(this);
      this.socket.onclose = this.handleClose.bind(this);
      this.socket.onerror = this.handleError.bind(this);
    } catch (error) {
      console.error('Error creating WebSocket connection:', error);
      this.scheduleReconnect();
    }
  }

  // Handle successful connection
  handleOpen() {
    console.log('WebSocket connection established');
    this.connectionActive = true; // Updated to use new property name
    this.reconnectAttempts = 0;
    
    // Send any pending messages
    while (this.pendingMessages.length > 0) {
      const message = this.pendingMessages.shift();
      this.sendMessage(message);
    }
    
    // Notify all 'connect' listeners
    this.notifyListeners('connect', { connected: true });
  }

  // Handle incoming messages
  handleMessage(event) {
    try {
      const data = JSON.parse(event.data);
      
      // Check if the message has a type
      if (data && data.type) {
        // Notify type-specific listeners
        this.notifyListeners(data.type, data.payload || {});
      }
      
      // Always notify general message listeners
      this.notifyListeners('message', data);
    } catch (error) {
      console.error('Error parsing WebSocket message:', error);
      // For non-JSON messages, pass the raw data
      this.notifyListeners('message', event.data);
    }
  }

  // Handle connection closed
  handleClose(event) {
    console.log(`WebSocket connection closed: ${event.code} ${event.reason}`);
    this.connectionActive = false; // Updated to use new property name
    
    // Notify all 'disconnect' listeners
    this.notifyListeners('disconnect', { 
      code: event.code, 
      reason: event.reason,
      wasClean: event.wasClean
    });
    
    // Try to reconnect if it wasn't a clean close
    if (!event.wasClean) {
      this.scheduleReconnect();
    }
  }

  // Handle connection errors
  handleError(error) {
    console.error('WebSocket error:', error);
    this.notifyListeners('error', { error });
  }

  // Schedule a reconnection attempt
  scheduleReconnect() {
    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      this.reconnectAttempts++;
      console.log(`Scheduling reconnect attempt ${this.reconnectAttempts} in ${this.reconnectInterval}ms`);
      
      setTimeout(() => {
        console.log(`Attempting to reconnect (${this.reconnectAttempts}/${this.maxReconnectAttempts})`);
        this.connect();
      }, this.reconnectInterval);
    } else {
      console.error(`Maximum reconnect attempts (${this.maxReconnectAttempts}) reached`);
      this.notifyListeners('reconnectFailed', { 
        attempts: this.reconnectAttempts,
        maxAttempts: this.maxReconnectAttempts
      });
    }
  }

  // Send a message to the server
  sendMessage(message) {
    const messageString = typeof message === 'string' 
      ? message 
      : JSON.stringify(message);
    
    if (this.connectionActive && this.socket) { // Updated to use new property name
      this.socket.send(messageString);
      return true;
    } else {
      console.warn('WebSocket not connected, queueing message for later delivery');
      this.pendingMessages.push(message);
      return false;
    }
  }

  // Add event listener
  on(eventType, callback) {
    if (!this.listeners.has(eventType)) {
      this.listeners.set(eventType, []);
    }
    
    this.listeners.get(eventType).push(callback);
    return this; // For method chaining
  }

  // Remove event listener
  off(eventType, callback) {
    if (!this.listeners.has(eventType)) return this;
    
    const callbacks = this.listeners.get(eventType);
    const index = callbacks.indexOf(callback);
    
    if (index !== -1) {
      callbacks.splice(index, 1);
    }
    
    return this; // For method chaining
  }

  // Notify all listeners of a specific event type
  notifyListeners(eventType, data) {
    if (!this.listeners.has(eventType)) return;
    
    for (const callback of this.listeners.get(eventType)) {
      try {
        callback(data);
      } catch (error) {
        console.error(`Error in ${eventType} listener:`, error);
      }
    }
  }

  // Close the connection
  disconnect() {
    if (this.socket) {
      this.socket.close(1000, 'Client disconnected');
      this.socket = null;
      this.connectionActive = false; // Updated to use new property name
    }
  }
  
  // Method to check connection status
  isConnected() {
    return this.connectionActive; // Return the renamed property
  }
}

// Create a singleton instance
const webSocketClient = new WebSocketClient();

export default webSocketClient;